import {
  getMedications,
  getPrescriptionsOrders,
  IMedication,
  IOrder,
  IPrescriptionsOrders,
} from 'API';
import { ORDER_STATUS } from 'Context/OrdersContext';
import { useAppSelector } from 'Hooks';
import { createContext, FC, useCallback, useEffect, useState } from 'react';

interface IPrescriptionAndUnHandledOrdersContext {
  prescriptionsAndUnHandledOrders: IPrescriptionsOrders;
  addOrder: (order: IOrder) => void;
  STATUS: ORDER_STATUS;
  medications: IMedication[];
}

export const PrescriptionAndUnHandledOrdersContext =
  createContext<IPrescriptionAndUnHandledOrdersContext>({
    prescriptionsAndUnHandledOrders: {
      prescriptions: [],
    },
    addOrder: () => {},
    STATUS: 'STARTED',
    medications: [],
  });

interface IPrescriptionAndUnHandledOrdersProvider {}

export const PrescriptionAndUnhandledOrdersProvider: FC<
  IPrescriptionAndUnHandledOrdersProvider
> = (props) => {
  const [prescriptionsAndUnHandledOrders, setPrescriptionsAndUnHandledOrders] =
    useState<IPrescriptionsOrders>({
      prescriptions: [],
    });

  const [medications, setMedications] = useState<IMedication[]>([]);

  const [status, setStatus] = useState<ORDER_STATUS>('STARTED');

  const initPrescriptionsAndUnHandledOrders = async () => {
    const prescriptionsAndUnHandledOrdersData = await getPrescriptionsOrders();

    if (prescriptionsAndUnHandledOrdersData) {
      setPrescriptionsAndUnHandledOrders({
        prescriptions: prescriptionsAndUnHandledOrdersData,
      });
    } else {
      setPrescriptionsAndUnHandledOrders({
        prescriptions: [],
      });
    }
  };

  const initMedications = async () => {
    const medicationsDto = await getMedications();

    if (medicationsDto) {
      setMedications(medicationsDto);
    } else {
      setMedications([]);
    }
  };

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const init = useCallback(async () => {
    setStatus('IN_PROGRESS');
    await Promise.all([
      initMedications(),
      initPrescriptionsAndUnHandledOrders(),
    ]);
    setStatus('FINISHED');
  }, []);

  useEffect(() => {
    init();
  }, [imposterId, init]);

  const addOrder = (order: IOrder) => {
    setPrescriptionsAndUnHandledOrders((prevState) => {
      const clonePrevState = { ...prevState };

      clonePrevState.prescriptions = [order, ...clonePrevState.prescriptions];

      return clonePrevState;
    });
  };

  return (
    <PrescriptionAndUnHandledOrdersContext.Provider
      value={{
        prescriptionsAndUnHandledOrders,
        addOrder,
        STATUS: status,
        medications,
      }}
    >
      {props.children}
    </PrescriptionAndUnHandledOrdersContext.Provider>
  );
};
